import { useRef, useState } from "react";
import { LogOut } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { StatefulButton, type ButtonState } from "./beui/StatefulButton";
import type { ButtonProps } from "./beui/Button";

type LogoutButtonProps = Pick<ButtonProps, "className" | "variant" | "size" | "disabled"> & {
  onLogout: () => Promise<void>;
  children?: string;
  iconOnly?: boolean;
};

/** Callers retain sign-out admission, errors and navigation. Rejections keep
 * the button available for retry; a resolved guard cancellation returns to idle. */
export function LogoutButton({ onLogout, children, iconOnly = false, ...props }: LogoutButtonProps) {
  const { t } = useI18n();
  const [state, setState] = useState<ButtonState>("idle");
  const pending = useRef(false);
  const label = children ?? t("auth.logout");
  const loadingText = t("auth.logout.busy");

  async function submitLogout() {
    if (pending.current) return;
    pending.current = true;
    setState("loading");
    try {
      await onLogout();
      setState("idle");
    } catch {
      setState("error");
    } finally {
      pending.current = false;
    }
  }

  const text = (value: string) => iconOnly ? <span className="sr-only">{value}</span> : value;
  return <StatefulButton {...props} type="button" state={state} onClick={() => void submitLogout()}
    aria-label={iconOnly ? state === "loading" ? loadingText : label : undefined}
    title={iconOnly ? label : undefined} loadingText={text(loadingText)} errorText={text(label)}
    icon={<LogOut className="h-4 w-4" aria-hidden="true" />} iconPosition="start">
    {text(label)}
  </StatefulButton>;
}
